import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as dropboxService from '@app/services/dropbox';
import { setAllTags, updateFile } from '@app/store/reducers/dropbox';

/**
 * Custom hook for tags management
 */
export const useTags = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Fetch all tags
  const fetchAllTags = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dropboxService.getAllTags();

      if (response?.success) {
        dispatch(setAllTags(response.data));
        return response.data;
      } else {
        const errorMsg = response?.message || 'Failed to fetch tags';
        toast.error(errorMsg);
        return [];
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'An error occurred while fetching tags';
      toast.error(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  // Add tags to file
  const addTagsToFile = useCallback(
    async (fileId: string, tags: string[]) => {
      try {
        setLoading(true);
        const response = await dropboxService.addTags(fileId, tags);

        if (response?.success) {
          dispatch(updateFile(response.data));
          toast.success('Tags added successfully');
          // Refresh all tags
          await fetchAllTags();
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to add tags';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while adding tags';
        toast.error(errorMsg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, fetchAllTags]
  );

  // Remove tag from file
  const removeTagFromFile = useCallback(
    async (fileId: string, tag: string) => {
      try {
        setLoading(true);
        const response = await dropboxService.removeTag(fileId, tag);

        if (response?.success) {
          dispatch(updateFile(response.data));
          toast.success('Tag removed successfully');
          // Refresh all tags
          await fetchAllTags();
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to remove tag';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while removing tag';
        toast.error(errorMsg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, fetchAllTags]
  );

  // Get file tags
  const getFileTags = useCallback(async (fileId: string) => {
    try {
      const response = await dropboxService.getFileTags(fileId);

      if (response?.success) {
        return response.data.tags || [];
      } else {
        return [];
      }
    } catch (error: any) {
      return [];
    }
  }, []);

  return {
    loading,
    fetchAllTags,
    addTagsToFile,
    removeTagFromFile,
    getFileTags,
  };
};
